import { type ReactNode, type FC } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { WhatWentWrongButton } from './WhatWentWrongButton';
import SearchResults from './SearchResults';
import { GlobalResultsButton } from './GlobalResultsButton';
import { Event, type Page } from '../../../../types';
import { Timestamp } from '../../../../services/Timestamp';
import { getEventFactory } from '../../../../utils/get-event-factory';
import { useAppInsights } from '../../../../hooks/useAppInsights';
import { ReactComponent as VesselIcon } from '../../../../assets/svg/Vessel.svg';
import { SearchContext, type VesselSearchResultViewModel } from '../../types';
import { canChangeContextToGlobal, formatDateTime } from '../../utils/search-utils';
import { useTripsContext } from '../../../GlobalContextProvider/GlobalContextProvider';
import { translateAndFormat } from '../../../../constants/translation';
import { isLive } from '../../../../utils/timestamp-utils';

interface Props {
  vesselResults: VesselSearchResultViewModel[];
  isLoading: boolean;
  searchPhrase: string;
  operation: string;
  timestamp: Timestamp;
  page: Page;
  onSearchClose: () => void;
  onVesselPositionReceived: (mmsi: number, operation: string, vesselName: string, timestamp?: Timestamp) => void;
  onWhatWentWrongClick: () => void;
  context: SearchContext;
  onGlobalResultsRequested: (searchPhrase: string) => void;
}

const VesselSearchResults: FC<Props> = ({
  vesselResults,
  isLoading,
  searchPhrase,
  operation,
  timestamp,
  page,
  onSearchClose,
  onVesselPositionReceived,
  onWhatWentWrongClick,
  context,
  onGlobalResultsRequested,
}) => {
  const appInsights = useAppInsights();
  const { operations } = useTripsContext();

  const isGlobalSearch = context === SearchContext.Global;

  const selectVessel = (vessel: VesselSearchResultViewModel): void => {
    const event = getEventFactory(Event.VesselFoundAndSelected)(
      page,
      operation,
      timestamp,
      searchPhrase,
      vessel.mmsi,
      vessel.name
    );
    appInsights.trackAnalyticsEvent(event);
    const vesselTimestamp = Timestamp.fromTimestampModel(vessel.timestamp);
    onVesselPositionReceived(
      Number(vessel.mmsi),
      vessel.operation,
      vessel.name,
      isLive(vesselTimestamp) ? null : Timestamp.fromTimestampModel(vessel.timestamp)
    );
    onSearchClose();
  };

  const vesselNodeFactory = (vessel: VesselSearchResultViewModel): ReactNode => {
    const timezone = operations.find(o => o.key === vessel.operation)?.timezone ?? 'UTC';
    return (
      <Box
        key={vessel.mmsi}
        className="result-item"
        onClick={() => {
          selectVessel(vessel);
        }}
      >
        <VesselIcon className="result-icon" />
        <div className="vessel-result">
          <div className="result-text">{vessel.name}</div>
          <div className="result-subtext text-sm-regular">
            <div className="subtext-left">{formatDateTime(vessel.timestamp, timezone)}</div>
            {context === SearchContext.Global && <div className="subtext-right">{vessel.operation}</div>}
          </div>
        </div>
      </Box>
    );
  };

  const showRedirectToGlobal = canChangeContextToGlobal(context);

  const noResultsMessage = (
    <div className="no-result-actions">
      <Text className="text-sm-regular">
        {isGlobalSearch
          ? translateAndFormat('NO_VESSELS_FOUND_FOR', searchPhrase)
          : translateAndFormat('NO_VESSELS_FOUND_IN', operation)}
      </Text>
      <WhatWentWrongButton onWhatWentWrongClick={onWhatWentWrongClick} />
      {showRedirectToGlobal && (
        <GlobalResultsButton searchPhrase={searchPhrase} onGlobalResultsRequested={onGlobalResultsRequested} />
      )}
    </div>
  );

  return (
    <SearchResults<VesselSearchResultViewModel>
      results={vesselResults}
      isLoading={isLoading}
      headerTranslationKey="VESSELS"
      resultNodeFactory={vesselNodeFactory}
      noResultsMessage={noResultsMessage}
    />
  );
};

export default VesselSearchResults;
